// Server-side progress tracking for lesson completion, video position,
// last-visited lesson, and quiz state. All functions require auth via
// requireSupabaseAuth and write to user-scoped tables protected by RLS.


import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { ALL_LESSONS, getLesson } from "@/data/lessons";

const LESSON_ID_RE = /^\d+\.\d+$/;

const lessonIdSchema = z
  .string()
  .min(1)
  .max(16)
  .regex(LESSON_ID_RE, "Invalid lesson_id format");

function assertKnownLesson(id: string): { moduleNumber: number } {
  const lesson = getLesson(id);
  if (!lesson) {
    throw new Error(`Unknown lesson_id: ${id}`);
  }
  return { moduleNumber: lesson.moduleNumber };
}

// ─── Mutations ────────────────────────────────────────────────────────────

export const markLessonComplete = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ lessonId: lessonIdSchema }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { moduleNumber } = assertKnownLesson(data.lessonId);

    // Idempotent: existing row wins (unique index on user_id + lesson_id).
    const { data: row, error } = await supabase
      .from("lesson_completions")
      .upsert(
        {
          user_id: userId,
          lesson_id: data.lessonId,
          module_number: moduleNumber,
          source: "lesson",
        },
        { onConflict: "user_id,lesson_id", ignoreDuplicates: false },
      )
      .select("completed_at")
      .single();

    if (error) throw new Error(error.message);
    return { completedAt: row.completed_at as string };
  });

export const saveVideoProgress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        lessonId: lessonIdSchema,
        position: z.number().min(0).max(24 * 60 * 60),
        duration: z.number().min(0).max(24 * 60 * 60).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    assertKnownLesson(data.lessonId);

    const { error } = await supabase
      .from("lesson_video_progress")
      .upsert(
        {
          user_id: userId,
          lesson_id: data.lessonId,
          position_seconds: Math.floor(data.position),
          duration_seconds:
            data.duration != null ? Math.floor(data.duration) : null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,lesson_id" },
      );

    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const migrateLocalCompletions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        lessonIds: z.array(lessonIdSchema).min(0).max(100),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Drop unknown ids defensively; never trust the client list.
    const rows = data.lessonIds
      .filter((id) => getLesson(id) !== undefined)
      .map((id) => ({
        user_id: userId,
        lesson_id: id,
        module_number: getLesson(id)!.moduleNumber,
        source: "migrated_localstorage" as const,
      }));

    if (rows.length === 0) return { inserted: 0 };

    // ignoreDuplicates: re-running the migration is a no-op for existing
    // completions, so the call is safe to retry.
    const { data: inserted, error } = await supabase
      .from("lesson_completions")
      .upsert(rows, {
        onConflict: "user_id,lesson_id",
        ignoreDuplicates: true,
      })
      .select("id");

    if (error) throw new Error(error.message);
    return { inserted: inserted?.length ?? 0 };
  });

// ─── Reads ────────────────────────────────────────────────────────────────

export const getMyCompletions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;

    const { data, error } = await supabase
      .from("lesson_completions")
      .select("lesson_id, completed_at");

    if (error) throw new Error(error.message);

    const map: Record<string, string> = {};
    for (const row of data ?? []) {
      map[row.lesson_id as string] = row.completed_at as string;
    }
    return map;
  });

export const getModuleProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ moduleNumber: z.number().int().min(1).max(99) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const total = ALL_LESSONS.filter(
      (l) => l.moduleNumber === data.moduleNumber,
    ).length;

    const { count, error } = await supabase
      .from("lesson_completions")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("module_number", data.moduleNumber);

    if (error) throw new Error(error.message);
    return { done: count ?? 0, total };
  });

export const getOverallProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;

    const { data, error } = await supabase
      .from("lesson_completions")
      .select("lesson_id");

    if (error) throw new Error(error.message);

    const completed = new Set((data ?? []).map((r) => r.lesson_id as string));
    const total = ALL_LESSONS.length;
    const next = ALL_LESSONS.find((l) => !completed.has(l.id));

    return {
      done: completed.size,
      total,
      nextLessonId: next?.id ?? null,
    };
  });

export const getVideoProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ lessonId: lessonIdSchema }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    assertKnownLesson(data.lessonId);

    const { data: row, error } = await supabase
      .from("lesson_video_progress")
      .select("position_seconds, duration_seconds")
      .eq("user_id", userId)
      .eq("lesson_id", data.lessonId)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!row) return null;

    return {
      position: (row.position_seconds as number) ?? 0,
      duration: (row.duration_seconds as number | null) ?? null,
    };
  });

// ─── Last lesson visited (cross-device Resume) ────────────────────────────

export const setLastLesson = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ lessonId: lessonIdSchema }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("profiles")
      .update({ last_lesson_id: data.lessonId })
      .eq("user_id", userId);
    if (error) {
      console.error("[progress] setLastLesson failed:", error);
      return { ok: false as const };
    }
    return { ok: true as const };
  });

export const getLastLesson = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ lessonId: string | null }> => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("profiles")
      .select("last_lesson_id")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) {
      console.error("[progress] getLastLesson failed:", error);
      return { lessonId: null };
    }
    return { lessonId: (data?.last_lesson_id as string | null) ?? null };
  });

// ─── Quiz state (partial answers persistence) ─────────────────────────────

type QuizAnswerValue = number | string | boolean | null;

const QuizStateSchema = z.object({
  lessonId: lessonIdSchema,
  answers: z.record(
    z.string().min(1).max(32),
    z.union([z.number(), z.string(), z.boolean(), z.null()]),
  ),
});


export const saveQuizState = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => QuizStateSchema.parse(input))
  .handler(async ({ data, context }): Promise<{ ok: boolean }> => {
    const { supabase, userId } = context;
    assertKnownLesson(data.lessonId);
    const { error } = await (supabase as any)
      .from("lesson_quiz_state")
      .upsert(
        {
          user_id: userId,
          lesson_id: data.lessonId,
          answers: data.answers,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,lesson_id" },
      );
    if (error) {
      console.error("[progress] saveQuizState failed:", error);
      return { ok: false };
    }
    return { ok: true };
  });

export const getQuizState = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ lessonId: lessonIdSchema }).parse(input),
  )
  .handler(async ({ data, context }): Promise<{ answers: Record<string, QuizAnswerValue> }> => {
    const { supabase, userId } = context;
    assertKnownLesson(data.lessonId);
    const { data: row, error } = await (supabase as any)
      .from("lesson_quiz_state")
      .select("answers")
      .eq("user_id", userId)
      .eq("lesson_id", data.lessonId)
      .maybeSingle();
    if (error || !row) {
      return { answers: {} };
    }
    return { answers: (row.answers ?? {}) as Record<string, QuizAnswerValue> };
  });


